"use client";
import React, { useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { toast } from "react-toastify";
import { apiVerifyCaptcha } from "@/apis/captcha";
import { apiSubscribeNewsletter } from "@/apis/page_optimization";

interface Props {
  domain: string;
  companyId: string;
}

const SubscribeForm = ({ domain, companyId }: Props) => {
  const [email, setEmail]   = useState("");
  const [status, setStatus] = useState<"idle"|"busy"|"done"|"fail">("idle");
  const { executeRecaptcha } = useGoogleReCaptcha();

  const isValid = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid(email)) { toast.error("Enter a valid email.", { autoClose: 2000 }); return; }
    if (!executeRecaptcha) { toast.error("reCAPTCHA not ready.", { autoClose: 2000 }); return; }
    setStatus("busy");
    try {
      const token  = await executeRecaptcha("newsletter_form");
      const capRes = await apiVerifyCaptcha(domain, token);
      if (!capRes?.verified) { setStatus("fail"); toast.error("Captcha failed.", { autoClose: 2000 }); return; }
      const res = await apiSubscribeNewsletter(companyId, email);
      if (res?.status === 200 || res?.message === "Subscribed successfully") {
        setStatus("done"); setEmail("");
        toast.success("You're subscribed!", { autoClose: 2000 });
      } else {
        toast.info("Already subscribed."); setStatus("idle");
      }
    } catch {
      toast.error("Something went wrong.", { autoClose: 2000 }); setStatus("fail");
    }
  };

  /* success */
  if (status === "done") {
    return (
      <div className="flex items-center gap-3 bg-emerald-500/10 border border-emerald-500/25 rounded-2xl px-6 py-4 w-full max-w-md">
        <div className="w-9 h-9 rounded-xl bg-emerald-500 flex items-center justify-center shrink-0 shadow-lg shadow-emerald-500/30">
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div>
          <p className="text-white font-black text-sm leading-none">You&apos;re subscribed!</p>
          <p className="text-slate-400 text-[12px] mt-1">Best deals heading your way daily.</p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="flex flex-col sm:flex-row items-stretch gap-3 w-full max-w-lg">

      {/* email input */}
      <div className="flex items-center gap-3 flex-1 bg-white/[0.07] border border-white/[0.12] hover:border-white/20 focus-within:border-blue-500/60 focus-within:bg-white/10 rounded-xl px-4 transition-all duration-300">
        <svg className="w-4 h-4 text-slate-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={status === "busy"}
          placeholder="Enter your email address"
          className="flex-1 h-11 bg-transparent outline-none text-white placeholder:text-slate-500 text-sm font-medium disabled:opacity-50"
        />
      </div>

      {/* submit */}
      <button
        type="submit"
        disabled={status === "busy"}
        className="group h-11 px-6 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/40 text-white font-black text-[11px] uppercase tracking-[0.2em] rounded-xl transition-all duration-300 active:scale-[0.97] flex items-center justify-center gap-2 shrink-0 shadow-lg shadow-blue-600/25 disabled:cursor-not-allowed"
      >
        {status === "busy" ? (
          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        ) : (
          <>
            {status === "fail" ? "Retry" : "Get Free Deals"}
            <svg className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14 5l7 7-7 7M3 12h18" />
            </svg>
          </>
        )}
      </button>

    </form>
  );
};

export default SubscribeForm;
